import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import { formatNumber } from '../../utils/constants';

const OrderEditDialog = ({
  open,
  onOpenChange,
  editingOrder,
  editOrderItems,
  editNotes,
  setEditNotes,
  priceList,
  onUpdateItem,
  onAddItem,
  onRemoveItem,
  onSubmit,
  onClose
}) => {
  return (
    <Dialog open={open} onOpenChange={(o) => {
      onOpenChange(o);
      if (!o) onClose();
    }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="edit-order-dialog">
        <DialogHeader>
          <DialogTitle>Edit Order - {editingOrder?.id?.substring(0, 8).toUpperCase()}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              Customer: <span className="font-medium text-foreground">{editingOrder?.customer_name}</span>
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">Order Items</label>
              <Button type="button" variant="outline" size="sm" onClick={onAddItem} data-testid="add-edit-item-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            </div>
            
            <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
              {editOrderItems.map((item, index) => (
                <div key={index} className="flex items-center space-x-2 p-2 bg-muted/30 rounded" data-testid="edit-order-item">
                  <Select
                    value={item.product_id}
                    onValueChange={(value) => onUpdateItem(index, 'product_id', value)}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {priceList.map(p => (
                        <SelectItem key={p.product.id} value={p.product.id}>
                          {p.product.name} - RWF {formatNumber(p.price)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Qty"
                    className="w-24"
                    value={item.quantity}
                    onChange={(e) => onUpdateItem(index, 'quantity', e.target.value)}
                  />
                  <span className="text-sm text-muted-foreground w-16">{item.unit || 'kg'}</span>
                  <span className="text-sm font-medium w-32 text-right">RWF {formatNumber(item.total)}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => onRemoveItem(index)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
              {editOrderItems.length === 0 && (
                <p className="text-center text-muted-foreground py-4">No items. Click "Add Item" to add products.</p>
              )}
            </div>
            
            <div className="mt-3 text-right">
              <span className="text-lg font-semibold">
                Total: RWF {formatNumber(editOrderItems.reduce((sum, item) => sum + (item.total || 0), 0))}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <Textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Additional notes..."
              rows={2}
            />
          </div>

          <div className="flex space-x-2">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" data-testid="edit-order-submit-btn">
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { OrderEditDialog };
